import axios from "axios";
import { clientCodeHeader } from "@entrilia/shared/api/clientApiContext";
import { parseSseStream } from "@entrilia/shared/api/sseStream";
import type { ApiResponse, BooleanOperationResult, JobProgress, JobStartResult } from "@entrilia/shared/api/types";
import { getBackendBaseUrl } from "@entrilia/shared/variables";
import type { QueryTraceStatus, StartQueryTraceRequest, StartQueryTraceResponse } from "./smartViewsTraceTypes";
import type {
  AgentInfo,
  AgentModelInfo,
  AgentSessionDetail,
  AgentSessionSummary,
  CopilotSseEvent,
  ResumeSessionResponse,
  StartAuthResponse,
  PollAuthResponse,
} from "./copilotAgentTypes";
import type {
  BcTenantCompanies,
  BcTenantSelection,
  ChangeClientEventHubsClientsResponse,
  ChangeClientEventHubsRequest,
  ChangeClientEventHubsResponse,
  ClientApplicationStateResponse,
  ClientProfileApiResponse,
  CopyCompanyRequest,
  CreateFundAdminClientRequest,
  CreateFundManagerClientRequest,
  CreateInvestorPortalUserFromContactRequest,
  CreateInvestorPortalUserResponse,
  DemoSourceTemplateCandidatesResponse,
  DeprovisionFundManagerClientRequest,
  ExcelAddinClientApplicationStateResponse,
  GetInvestorPortalUserStatusRequest,
  GetInvestorPortalUserStatusResponse,
  OrganizationInfo,
  ResetInvestorPortalUserPasswordRequest,
  ResetInvestorPortalUserPasswordResponse,
  SetApplicationStateRequest,
  SetDemoSourceTemplateRequest,
  SherWebOrganizationInfo,
  SyncFundAdminBcPermissionsRequest,
  StartDwhSyncRequest,
  StartDwhSyncResponse,
  UserExistsRequest,
  UserExistsResponse,
} from "./clientsManagementTypes";
import type {
  GetOrganizationsResponse,
  GetOrganizationUsersRequest,
  GetOrganizationUsersResponse,
  RenameUserRequest,
  RenameUserResponse,
  ResetEntriliaGuestUserMfaRequest,
  ResetEntriliaGuestUserMfaResponse,
  ResetUserMfaRequest,
  ResetUserMfaResponse,
} from "./clientUsersTypes";
import type {
  ClientEmailAccountsResponse,
  ClientEmailDomainsResponse,
  CreateEmailAccountRequest,
  EmailAccountDeletionRequest,
  EmailAccountDeletionResponse,
  EmailAccountSmtpLogin,
  OrganizationEmailAccountsResponse,
  ProvisionedEmailAccount,
  StartTestEmailRequest,
} from "./emailAccountsTypes";
import type { ClearCachesResponse } from "./systemMaintenanceTypes";
import type { UserInfo, UserPermissions } from "./userTypes";
import type {
  ObserverIssueInfo,
  ObserverAssignableUserInfo,
  ObserverRunInfo,
  SetObserverIssueAssigneeRequest,
  SetObserverIssueGithubNumberRequest,
  SetObserverIssueAcknowledgementRequest,
  SetObserverIssueStatusRequest,
  StartObserverInvestigationRequest,
  StartObserverInvestigationResponse,
} from "./observerTypes";

const baseUri = getBackendBaseUrl("operations");

export const api = {
  identity: {
    getIdentity: async (params: { loadLargeAvatarImage: boolean }) => {
      const { data } = await axios.get<ApiResponse<UserInfo>>(`${baseUri}/identity`, { params });
      return data;
    },
    getUserPermissions: async () => {
      const { data } = await axios.get<ApiResponse<UserPermissions>>(`${baseUri}/identity/permissions`);
      return data;
    },
  },
  systemMaintenance: {
    clearCaches: async () => {
      const { data } = await axios.put<ApiResponse<ClearCachesResponse>>(
        `${baseUri}/systemMaintenance/clear-caches`,
        null,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  clientProvisioning: {
    provisionFundAdminClient: async (payload: CreateFundAdminClientRequest) => {
      const { data } = await axios.post<ApiResponse<JobStartResult>>(
        `${baseUri}/clientProvisioning/fund-admin`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    provisionFundManagerClient: async (payload: CreateFundManagerClientRequest) => {
      const { data } = await axios.post<ApiResponse<JobStartResult>>(
        `${baseUri}/clientProvisioning/fund-manager`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getJobProgress: async (operationId: string) => {
      const { data } = await axios.get<ApiResponse<JobProgress>>(`${baseUri}/clientProvisioning/jobs/${operationId}`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    cancelJob: async (operationId: string) => {
      const { data } = await axios.post<ApiResponse<null>>(
        `${baseUri}/clientProvisioning/jobs/${operationId}/cancel`,
        undefined,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getExistingOrganizations: async () => {
      const { data } = await axios.get<ApiResponse<OrganizationInfo[]>>(`${baseUri}/clientProvisioning/organizations`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    getExistingBcCompanies: async (organizationClientCode: string, tenant?: BcTenantSelection) => {
      const { data } = await axios.get<ApiResponse<BcTenantCompanies>>(`${baseUri}/clientProvisioning/bc-companies`, {
        headers: clientCodeHeader(),
        params: { organizationClientCode, tenantId: tenant?.tenantId, environment: tenant?.environment },
      });
      return data;
    },
    getSherWebOrganizations: async () => {
      const { data } = await axios.get<ApiResponse<SherWebOrganizationInfo[]>>(
        `${baseUri}/clientProvisioning/sherweb-organizations`,
        {
          headers: clientCodeHeader(),
        }
      );
      return data;
    },
    syncFundAdminBcPermissions: async (payload: SyncFundAdminBcPermissionsRequest) => {
      const { data } = await axios.post<ApiResponse<void>>(
        `${baseUri}/clientProvisioning/fund-admin/sync-bc-permissions`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  usersManagement: {
    createInvestorPortalUserFromContact: async (payload: CreateInvestorPortalUserFromContactRequest) => {
      const { data } = await axios.post<ApiResponse<CreateInvestorPortalUserResponse>>(
        `${baseUri}/usersManagement/create-investor-portal-user-from-contact`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    userExists: async (payload: UserExistsRequest) => {
      const { data } = await axios.post<ApiResponse<UserExistsResponse>>(
        `${baseUri}/usersManagement/user-exists`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getInvestorPortalUserStatus: async (payload: GetInvestorPortalUserStatusRequest) => {
      const { data } = await axios.post<ApiResponse<GetInvestorPortalUserStatusResponse>>(
        `${baseUri}/usersManagement/get-investor-portal-user-status`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    resetInvestorPortalUserPassword: async (payload: ResetInvestorPortalUserPasswordRequest) => {
      const { data } = await axios.post<ApiResponse<ResetInvestorPortalUserPasswordResponse>>(
        `${baseUri}/usersManagement/reset-investor-portal-user-password`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  dwhSync: {
    getSyncClients: async () => {
      const { data } = await axios.get<ApiResponse<string[]>>(`${baseUri}/dwhSync/sync-clients`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    startSync: async (payload: StartDwhSyncRequest) => {
      const { data } = await axios.post<ApiResponse<StartDwhSyncResponse>>(`${baseUri}/dwhSync/start`, payload, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    getClientsWithEventHubs: async () => {
      const { data } = await axios.get<ApiResponse<ChangeClientEventHubsClientsResponse>>(
        `${baseUri}/dwhSync/clients`,
        {
          headers: clientCodeHeader(),
        }
      );
      return data;
    },
    changeClientEventHubs: async (payload: ChangeClientEventHubsRequest) => {
      const { data } = await axios.put<ApiResponse<ChangeClientEventHubsResponse>>(
        `${baseUri}/dwhSync/change-client-event-hubs`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  copyCompany: {
    startCopyCompany: async (payload: CopyCompanyRequest) => {
      const { data } = await axios.put<ApiResponse<JobStartResult>>(`${baseUri}/CopyCompany`, payload, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    getCopyCompanyProgress: async (operationId: string) => {
      const { data } = await axios.get<ApiResponse<JobProgress>>(`${baseUri}/CopyCompany/progress/${operationId}`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
  },
  clientUsers: {
    getOrganizations: async () => {
      const { data } = await axios.get<ApiResponse<GetOrganizationsResponse>>(`${baseUri}/ClientUsers/organizations`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    resetUserMfa: async (payload: ResetUserMfaRequest) => {
      const { data } = await axios.post<ApiResponse<ResetUserMfaResponse>>(
        `${baseUri}/ClientUsers/reset-user-mfa`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getOrganizationUsers: async (payload: GetOrganizationUsersRequest) => {
      const { data } = await axios.post<ApiResponse<GetOrganizationUsersResponse>>(
        `${baseUri}/ClientUsers/organization-users`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    renameUser: async (payload: RenameUserRequest) => {
      const { data } = await axios.post<ApiResponse<RenameUserResponse>>(
        `${baseUri}/ClientUsers/rename-user`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  entriliaUsers: {
    resetGuestUserMfa: async (payload: ResetEntriliaGuestUserMfaRequest) => {
      const { data } = await axios.post<ApiResponse<ResetEntriliaGuestUserMfaResponse>>(
        `${baseUri}/EntriliaUsers/reset-guest-user-mfa`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  clientProfiles: {
    getClientProfiles: async () => {
      const { data } = await axios.get<ApiResponse<ClientProfileApiResponse[]>>(`${baseUri}/ClientProfile`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    setInvestorPortalState: async (payload: SetApplicationStateRequest) => {
      const { data } = await axios.put<ApiResponse<ClientApplicationStateResponse>>(
        `${baseUri}/ClientProfile/investor-portal/state`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setExpenseManagementState: async (payload: SetApplicationStateRequest) => {
      const { data } = await axios.put<ApiResponse<ClientApplicationStateResponse>>(
        `${baseUri}/ClientProfile/expense-management/state`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setEntriliaMcpState: async (payload: SetApplicationStateRequest) => {
      const { data } = await axios.put<ApiResponse<ClientApplicationStateResponse>>(
        `${baseUri}/ClientProfile/entrilia-mcp/state`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setPortfolioMonitoringState: async (payload: SetApplicationStateRequest) => {
      const { data } = await axios.put<ApiResponse<ClientApplicationStateResponse>>(
        `${baseUri}/ClientProfile/portfolio-monitoring/state`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getDemoSourceTemplateCandidates: async (targetClientCode: string) => {
      const { data } = await axios.get<ApiResponse<DemoSourceTemplateCandidatesResponse>>(
        `${baseUri}/DemoRefreshTemplateLink/candidates/${encodeURIComponent(targetClientCode)}`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setDemoSourceTemplate: async (payload: SetDemoSourceTemplateRequest) => {
      const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
        `${baseUri}/DemoRefreshTemplateLink`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setExcelAddinUseBcReportGenerationViaSession: async (payload: SetApplicationStateRequest) => {
      const { data } = await axios.put<ApiResponse<ExcelAddinClientApplicationStateResponse>>(
        `${baseUri}/ClientProfile/excel-addin/use-bc-report-generation-via-session`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  emailAccounts: {
    getOrganizationEmailAccounts: async (organizationCode: string) => {
      const { data } = await axios.get<ApiResponse<OrganizationEmailAccountsResponse>>(
        `${baseUri}/EmailAccounts/organizations/${encodeURIComponent(organizationCode)}`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getClientEmailAccounts: async (clientCode: string) => {
      const { data } = await axios.get<ApiResponse<ClientEmailAccountsResponse>>(
        `${baseUri}/EmailAccounts/clients/${encodeURIComponent(clientCode)}`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getOrganizationSmtpLogins: async (organizationCode: string) => {
      const { data } = await axios.get<ApiResponse<EmailAccountSmtpLogin[]>>(
        `${baseUri}/EmailAccounts/organizations/${encodeURIComponent(organizationCode)}/smtp-logins`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getClientSmtpLogins: async (clientCode: string) => {
      const { data } = await axios.get<ApiResponse<EmailAccountSmtpLogin[]>>(
        `${baseUri}/EmailAccounts/clients/${encodeURIComponent(clientCode)}/smtp-logins`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    startTestEmail: async (clientCode: string, accountId: string, payload: StartTestEmailRequest) => {
      const { data } = await axios.post<ApiResponse<JobStartResult>>(
        `${baseUri}/EmailAccounts/clients/${encodeURIComponent(clientCode)}/accounts/${encodeURIComponent(accountId)}/test-emails`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getClientEmailDomains: async (clientCode: string) => {
      const { data } = await axios.get<ApiResponse<ClientEmailDomainsResponse>>(
        `${baseUri}/EmailAccounts/clients/${encodeURIComponent(clientCode)}/domains`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    createEmailAccount: async (clientCode: string, payload: CreateEmailAccountRequest) => {
      const { data } = await axios.post<ApiResponse<ProvisionedEmailAccount>>(
        `${baseUri}/EmailAccounts/clients/${encodeURIComponent(clientCode)}/accounts`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    resetSmtpPassword: async (clientCode: string, accountId: string) => {
      const { data } = await axios.post<ApiResponse<ProvisionedEmailAccount>>(
        `${baseUri}/EmailAccounts/clients/${encodeURIComponent(clientCode)}/accounts/${encodeURIComponent(accountId)}/smtp-password/reset`,
        undefined,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    deleteEmailAccount: async (clientCode: string, accountId: string, request: EmailAccountDeletionRequest) => {
      const { data } = await axios.post<ApiResponse<EmailAccountDeletionResponse>>(
        `${baseUri}/EmailAccounts/clients/${encodeURIComponent(clientCode)}/accounts/${encodeURIComponent(accountId)}/delete`,
        request,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getTestEmailProgress: async (operationId: string) => {
      const { data } = await axios.get<ApiResponse<JobProgress>>(
        `${baseUri}/EmailAccounts/test-emails/${encodeURIComponent(operationId)}`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  clientDeprovisioning: {
    deprovisionFundManagerClient: async (payload: DeprovisionFundManagerClientRequest) => {
      const { data } = await axios.post<ApiResponse<JobStartResult>>(
        `${baseUri}/clientDeprovisioning/fund-manager/deprovision`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getJobProgress: async (operationId: string) => {
      const { data } = await axios.get<ApiResponse<JobProgress>>(
        `${baseUri}/clientDeprovisioning/jobs/${operationId}`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    cancelJob: async (operationId: string) => {
      const { data } = await axios.post<ApiResponse<null>>(
        `${baseUri}/clientDeprovisioning/jobs/${operationId}/cancel`,
        undefined,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  copilotAgents: {
    listAgents: async () => {
      const { data } = await axios.get<ApiResponse<AgentInfo[]>>(`${baseUri}/copilot-agents`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    listModels: async () => {
      const { data } = await axios.get<ApiResponse<AgentModelInfo[]>>(`${baseUri}/copilot-agents/models`, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    startAuth: async () => {
      const { data } = await axios.post<ApiResponse<StartAuthResponse>>(`${baseUri}/copilot-agents/auth/start`, null, {
        headers: clientCodeHeader(),
      });
      return data;
    },
    pollAuth: async (deviceCode: string) => {
      const { data } = await axios.post<ApiResponse<PollAuthResponse>>(
        `${baseUri}/copilot-agents/auth/poll`,
        { deviceCode },
        { headers: clientCodeHeader() }
      );
      return data;
    },
    createSession: async (agentSlug: string, model?: string, effort?: string) => {
      const params: { model?: string; effort?: string } = {};
      if (model) {
        params.model = model;
      }
      if (effort) {
        params.effort = effort;
      }
      const { data } = await axios.post<ApiResponse<{ sessionId: string | null; requiresGitHubAuth: boolean }>>(
        `${baseUri}/copilot-agents/${agentSlug}/sessions`,
        null,
        { headers: clientCodeHeader(), params: params.model || params.effort ? params : undefined }
      );
      return data;
    },
    listSessions: async (agentSlug: string) => {
      const { data } = await axios.get<ApiResponse<AgentSessionSummary[]>>(
        `${baseUri}/copilot-agents/${agentSlug}/sessions`,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    findSessionsByFirstMessage: async (agentSlug: string, firstMessage: string) => {
      const { data } = await axios.post<ApiResponse<AgentSessionSummary[]>>(
        `${baseUri}/copilot-agents/${agentSlug}/sessions/by-first-message`,
        { firstMessageUserInput: firstMessage },
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getSession: async (sessionId: string) => {
      const { data } = await axios.get<ApiResponse<AgentSessionDetail>>(
        `${baseUri}/copilot-agents/sessions/${sessionId}`,
        {
          headers: clientCodeHeader(),
        }
      );
      return data;
    },
    resumeSession: async (sessionId: string) => {
      const { data } = await axios.post<ApiResponse<ResumeSessionResponse>>(
        `${baseUri}/copilot-agents/sessions/${sessionId}/resume`,
        null,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    deleteSession: async (sessionId: string) => {
      const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
        `${baseUri}/copilot-agents/sessions/${sessionId}`,
        {
          headers: clientCodeHeader(),
        }
      );
      return data;
    },
    sendMessageStream: async function* (
      sessionId: string,
      message: string,
      signal?: AbortSignal
    ): AsyncGenerator<CopilotSseEvent> {
      const response = await axios.post<ReadableStream>(
        `${baseUri}/copilot-agents/sessions/${sessionId}/messages`,
        { message },
        { headers: clientCodeHeader(), responseType: "stream", adapter: "fetch", signal }
      );
      yield* parseSseStream<CopilotSseEvent>(response.data);
    },
  },
  observer: {
    listIssues: async (params?: { limit?: number; fromRunAtUtc?: string }, signal?: AbortSignal) => {
      const { data } = await axios.get<ApiResponse<ObserverIssueInfo[]>>(`${baseUri}/observer/issues`, {
        headers: clientCodeHeader(),
        params,
        signal,
      });
      return data;
    },
    getRun: async (runId: string, signal?: AbortSignal) => {
      const { data } = await axios.get<ApiResponse<ObserverRunInfo>>(`${baseUri}/observer/runs/${runId}`, {
        headers: clientCodeHeader(),
        signal,
      });
      return data;
    },
    listAssignableUsers: async (signal?: AbortSignal) => {
      const { data } = await axios.get<ApiResponse<ObserverAssignableUserInfo[]>>(`${baseUri}/observer/assignees`, {
        headers: clientCodeHeader(),
        signal,
      });
      return data;
    },
    setIssueStatus: async (issueId: string, payload: SetObserverIssueStatusRequest) => {
      const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
        `${baseUri}/observer/issues/${issueId}/status`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setIssueAcknowledgement: async (issueId: string, payload: SetObserverIssueAcknowledgementRequest) => {
      const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
        `${baseUri}/observer/issues/${issueId}/acknowledgement`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setIssueAssignee: async (issueId: string, payload: SetObserverIssueAssigneeRequest) => {
      const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
        `${baseUri}/observer/issues/${issueId}/assignee`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setIssueGithubNumber: async (issueId: string, payload: SetObserverIssueGithubNumberRequest) => {
      const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
        `${baseUri}/observer/issues/${issueId}/github-issue-number`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    startInvestigation: async (issueId: string, payload: StartObserverInvestigationRequest) => {
      const { data } = await axios.post<ApiResponse<StartObserverInvestigationResponse>>(
        `${baseUri}/observer/issues/${issueId}/investigations`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
  },
  smartViewsTrace: {
    start: async (payload: StartQueryTraceRequest) => {
      const { data } = await axios.post<ApiResponse<StartQueryTraceResponse>>(
        `${baseUri}/smartviews-trace/start`,
        payload,
        { headers: clientCodeHeader() }
      );
      return data;
    },
    stop: async (clientCode: string, userEmail: string) => {
      const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
        `${baseUri}/smartviews-trace/stop`,
        { clientCode, userEmail },
        { headers: clientCodeHeader() }
      );
      return data;
    },
    setOptions: async (clientCode: string, userEmail: string, includeResults: boolean) => {
      const { data } = await axios.post<ApiResponse<QueryTraceStatus>>(
        `${baseUri}/smartviews-trace/options`,
        { clientCode, userEmail, includeResults },
        { headers: clientCodeHeader() }
      );
      return data;
    },
    getSubscriptionToken: async (clientCode: string, userEmail: string) => {
      const { data } = await axios.get<ApiResponse<string>>(`${baseUri}/smartviews-trace/subscription-token`, {
        headers: clientCodeHeader(),
        params: { clientCode, userEmail },
      });
      return data;
    },
  },
};
